import axios from "axios";
import { forceLogout, getStoredToken, isTokenExpired } from "./auth";

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const EVENT_TEAMS_API_URL = `${API_BASE_URL}/event-teams`;
const JOB_TYPES_API_URL = `${API_BASE_URL}/job-types`;
const BACKEND_BASE_URL =
  process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:5000";

// Helper to get full image URL
export const getImageUrl = (url) => {
  if (!url) return null;
  // If URL is already absolute, return as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  // If URL starts with /, it's a relative path from backend
  if (url.startsWith("/")) {
    return `${BACKEND_BASE_URL}${url}`;
  }
  // Otherwise, assume it's a relative path
  return `${BACKEND_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Helper function to get auth token
const getAuthToken = () => getStoredToken();

/**
 * End the session as soon as the token is known to be dead.
 *
 * Called both before a request goes out (the stored token's own `exp` has passed) and after one
 * comes back flagged TOKEN_EXPIRED by the server. Returns true when a logout was triggered.
 */
const endSessionIfTokenExpired = () => {
  const token = getAuthToken();
  if (!token || !isTokenExpired(token)) return false;
  forceLogout({ expired: true });
  return true;
};

// The job-type endpoints below go through axios rather than apiRequest; give them the same
// end-the-session-on-expiry behaviour.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error?.response?.data;
    if (error?.response?.status === 401 && data?.code === "TOKEN_EXPIRED") {
      forceLogout({ expired: true });
    }
    return Promise.reject(error);
  }
);

// Helper function to get headers
const getHeaders = (includeAuth = true) => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
};

// API request helper
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      ...getHeaders(options.includeAuth !== false),
      ...options.headers,
    },
  };

  // Don't bother sending a token we already know has lapsed - log out and let the caller fail.
  if (options.includeAuth !== false && endSessionIfTokenExpired()) {
    const error = new Error("Your session has expired. Please log in again.");
    error.status = 401;
    error.isPermissionError = true;
    error.isSessionExpired = true;
    throw error;
  }

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || "An error occurred");
      error.status = response.status;
      // 401/403 are expected when a role lacks a feature — callers can stay silent.
      error.isPermissionError = response.status === 401 || response.status === 403;
      // A 401 the server tagged TOKEN_EXPIRED is the session dying, not a missing permission.
      error.isSessionExpired = response.status === 401 && data?.code === "TOKEN_EXPIRED";
      if (error.isSessionExpired) {
        forceLogout({ expired: true });
      }
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// API methods
export const api = {
  // Auth
  login: async (email, password) => {
    return apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      includeAuth: false,
    });
  },

  register: async (userData) => {
    return apiRequest("/auth/register", {
      method: "POST",
      body: JSON.stringify(userData),
      includeAuth: false,
    });
  },

  getCurrentUser: async () => {
    return apiRequest("/auth/me");
  },

  changePassword: async (currentPassword, newPassword) => {
    return apiRequest("/auth/change-password", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  logout: async () => {
    return apiRequest("/auth/logout");
  },

  // Assets
  getAssets: async (params = {}) => {
    // Build query string from params object
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/assets${queryString ? `?${queryString}` : ""}`);
  },

  getAsset: async (id) => {
    return apiRequest(`/assets/${id}`);
  },

  createAsset: async (assetData) => {
    return apiRequest("/assets", {
      method: "POST",
      body: JSON.stringify(assetData),
    });
  },

  updateAsset: async (id, assetData) => {
    return apiRequest(`/assets/${id}`, {
      method: "PUT",
      body: JSON.stringify(assetData),
    });
  },

  bulkUpdateCondition: async (assetIds, condition) => {
    return apiRequest("/assets/bulk/condition", {
      method: "PUT",
      body: JSON.stringify({ assetIds, condition }),
    });
  },

  uploadAssetPhoto: async (id, files) => {
    const formData = new FormData();

    // Handle both single file and array of files
    const filesArray = Array.isArray(files) ? files : [files];

    // Append all files to FormData with the same field name 'photos'
    filesArray.forEach((file) => {
      formData.append("photos", file);
    });

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const token = user.token;

    const response = await fetch(`${API_BASE_URL}/assets/${id}/upload-photo`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    return data;
  },

  deleteAssetPhoto: async (id, photoId) => {
    return apiRequest(`/assets/${id}/photos/${photoId}`, {
      method: "DELETE",
    });
  },

  deleteAsset: async (id) => {
    return apiRequest(`/assets/${id}`, {
      method: "DELETE",
    });
  },

  bulkDeleteAssets: async (assetIds) => {
    return apiRequest("/assets/bulk", {
      method: "DELETE",
      body: JSON.stringify({ assetIds }),
    });
  },

  getDeletedAssets: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/assets/trash${queryString ? `?${queryString}` : ""}`);
  },

  restoreAsset: async (id) => {
    return apiRequest(`/assets/${id}/restore`, {
      method: "PUT",
    });
  },

  permanentlyDeleteAsset: async (id) => {
    return apiRequest(`/assets/${id}/permanent`, {
      method: "DELETE",
    });
  },

  checkoutAsset: async (id, checkoutData, photos = []) => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const token = user.token;

    const formData = new FormData();
    
    // Append photos if provided
    if (photos && photos.length > 0) {
      photos.forEach((photo) => {
        if (photo.file) {
          formData.append("photos", photo.file);
        }
      });
    }
    
    // Append other checkout data as JSON string
    Object.keys(checkoutData).forEach((key) => {
      if (checkoutData[key] !== undefined && checkoutData[key] !== null) {
        formData.append(key, typeof checkoutData[key] === 'object' 
          ? JSON.stringify(checkoutData[key]) 
          : checkoutData[key]);
      }
    });

    const response = await fetch(`${API_BASE_URL}/assets/${id}/checkout`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "An error occurred");
    }
    return data;
  },

  checkinAsset: async (id, checkinData, photos = []) => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const token = user.token;

    const formData = new FormData();
    
    // Append photos if provided
    if (photos && photos.length > 0) {
      photos.forEach((photo) => {
        if (photo.file) {
          formData.append("photos", photo.file);
        }
      });
    }
    
    // Append other checkin data as JSON string
    Object.keys(checkinData).forEach((key) => {
      if (checkinData[key] !== undefined && checkinData[key] !== null) {
        formData.append(key, typeof checkinData[key] === 'object' 
          ? JSON.stringify(checkinData[key]) 
          : checkinData[key]);
      }
    });

    const response = await fetch(`${API_BASE_URL}/assets/${id}/checkin`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "An error occurred");
    }
    return data;
  },

  getAssetHistory: async (id) => {
    return apiRequest(`/assets/${id}/history`);
  },

  getRecentActivity: async () => {
    return apiRequest("/assets/activity/recent");
  },

  transferAsset: async (id, transferData) => {
    return apiRequest(`/assets/${id}/transfer`, {
      method: "POST",
      body: JSON.stringify(transferData),
    });
  },

  // Projects
  getProjects: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/projects${queryString ? `?${queryString}` : ""}`);
  },

  getProject: async (id) => {
    return apiRequest(`/projects/${id}`);
  },

  getTask: async (taskId) => {
    return apiRequest(`/projects/tasks/${taskId}`);
  },

  getTasks: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/projects/tasks${queryString ? `?${queryString}` : ""}`);
  },

  getTaskWorklogReport: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/projects/reports/task-worklogs${queryString ? `?${queryString}` : ""}`);
  },

  getTaskSummaryByUser: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/projects/tasks/summary${queryString ? `?${queryString}` : ""}`);
  },

  getTaskSummaryByProject: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== "") {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/projects/tasks/summary-by-project${queryString ? `?${queryString}` : ""}`);
  },

  updateTask: async (taskId, taskData) => {
    return apiRequest(`/projects/tasks/${taskId}`, {
      method: "PUT",
      body: JSON.stringify(taskData),
    });
  },

  deleteTask: async (taskId) => {
    return apiRequest(`/projects/tasks/${taskId}`, {
      method: "DELETE",
    });
  },

  uploadTaskAttachments: async (taskId, files) => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const token = user.token;
    const formData = new FormData();
    const filesArray = Array.isArray(files) ? files : [files];
    filesArray.forEach((file) => formData.append("files", file));

    const response = await fetch(`${API_BASE_URL}/projects/tasks/${taskId}/attachments`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to upload attachments");
    }
    return data;
  },

  deleteTaskAttachment: async (taskId, attachmentId) => {
    return apiRequest(`/projects/tasks/${taskId}/attachments/${attachmentId}`, {
      method: "DELETE",
    });
  },

  addTaskComment: async (taskId, text) => {
    return apiRequest(`/projects/tasks/${taskId}/comments`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  },

  // Send a manual reminder (in-app + email) to a task's assignees.
  sendTaskReminder: async (taskId, options = {}) => {
    // Backward compatible: a plain string is treated as the note.
    const { note = "", recipientIds } =
      typeof options === "string" ? { note: options } : options || {};
    const body = { note };
    if (Array.isArray(recipientIds)) body.recipientIds = recipientIds;
    return apiRequest(`/projects/tasks/${taskId}/remind`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  createProject: async (projectData) => {
    return apiRequest("/projects", {
      method: "POST",
      body: JSON.stringify(projectData),
    });
  },

  updateProject: async (id, projectData) => {
    return apiRequest(`/projects/${id}`, {
      method: "PUT",
      body: JSON.stringify(projectData),
    });
  },

  deleteProject: async (id) => {
    return apiRequest(`/projects/${id}`, {
      method: "DELETE",
    });
  },

  // ---- Trash: projects ----
  getDeletedProjects: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/projects/trash${queryString ? `?${queryString}` : ""}`);
  },

  // Tasks that were trashed together with a trashed project (for the
  // restore-selection modal).
  getProjectTrashedTasks: async (id) => {
    return apiRequest(`/projects/${id}/trashed-tasks`);
  },

  // `taskIds` (optional): when provided, only those cascade-trashed tasks are
  // restored; the rest stay in Trash. Omit to restore the project's tasks all.
  restoreProject: async (id, taskIds) => {
    return apiRequest(`/projects/${id}/restore`, {
      method: "PUT",
      body: Array.isArray(taskIds) ? JSON.stringify({ taskIds }) : undefined,
    });
  },

  permanentlyDeleteProject: async (id) => {
    return apiRequest(`/projects/${id}/permanent`, {
      method: "DELETE",
    });
  },

  // ---- Trash: tasks ----
  getDeletedTasks: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/projects/tasks/trash${queryString ? `?${queryString}` : ""}`);
  },

  restoreTask: async (taskId) => {
    return apiRequest(`/projects/tasks/${taskId}/restore`, {
      method: "PUT",
    });
  },

  permanentlyDeleteTask: async (taskId) => {
    return apiRequest(`/projects/tasks/${taskId}/permanent`, {
      method: "DELETE",
    });
  },

  addProjectTask: async (id, taskData) => {
    return apiRequest(`/projects/${id}/tasks`, {
      method: "POST",
      body: JSON.stringify(taskData),
    });
  },

  addTask: async (taskData) => {
    return apiRequest('/projects/tasks', {
      method: "POST",
      body: JSON.stringify(taskData),
    });
  },

  addProjectFeature: async (id, featureData) => {
    return apiRequest(`/projects/${id}/features`, {
      method: "POST",
      body: JSON.stringify(featureData),
    });
  },

  updateProjectFeature: async (id, featureId, featureData) => {
    return apiRequest(`/projects/${id}/features/${featureId}`, {
      method: "PUT",
      body: JSON.stringify(featureData),
    });
  },

  deleteProjectFeature: async (id, featureId) => {
    return apiRequest(`/projects/${id}/features/${featureId}`, {
      method: "DELETE",
    });
  },

  addProjectFile: async (id, fileData) => {
    return apiRequest(`/projects/${id}/files`, {
      method: "POST",
      body: JSON.stringify(fileData),
    });
  },

  uploadProjectFiles: async (id, files) => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const token = user.token;
    const formData = new FormData();
    const filesArray = Array.isArray(files) ? files : [files];
    filesArray.forEach((file) => formData.append("files", file));

    const response = await fetch(`${API_BASE_URL}/projects/${id}/files/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to upload project files");
    }
    return data;
  },

  downloadAllProjectFiles: async (id) => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const token = user.token;
    const response = await fetch(`${API_BASE_URL}/projects/${id}/files/download-all`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let message = "Failed to download files";
      try {
        const data = await response.json();
        message = data.message || message;
      } catch (_) {
        // Ignore JSON parse errors for non-JSON error responses.
      }
      throw new Error(message);
    }

    const disposition = response.headers.get("content-disposition") || "";
    const fallbackName = `project-files-${id}.zip`;
    const extractedName = disposition.match(/filename="([^"]+)"/i)?.[1] || fallbackName;
    const blob = await response.blob();
    return { blob, fileName: extractedName };
  },

  deleteProjectFile: async (id, fileId) => {
    return apiRequest(`/projects/${id}/files/${fileId}`, {
      method: "DELETE",
    });
  },

  addProjectFileComment: async (id, fileId, text) => {
    return apiRequest(`/projects/${id}/files/${fileId}/comments`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  },

  updateProjectFileComment: async (id, fileId, commentId, text) => {
    return apiRequest(`/projects/${id}/files/${fileId}/comments/${commentId}`, {
      method: "PUT",
      body: JSON.stringify({ text }),
    });
  },

  deleteProjectFileComment: async (id, fileId, commentId) => {
    return apiRequest(`/projects/${id}/files/${fileId}/comments/${commentId}`, {
      method: "DELETE",
    });
  },

  updateProjectNotes: async (id, notes) => {
    return apiRequest(`/projects/${id}/notes`, {
      method: "PUT",
      body: JSON.stringify({ notes }),
    });
  },

  // Users
  getUsers: async (params = {}) => {
    // Build query string from params object
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/users${queryString ? `?${queryString}` : ""}`);
  },

  getUser: async (id) => {
    return apiRequest(`/users/${id}`);
  },

  createUser: async (userData) => {
    return apiRequest("/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  },

  updateUser: async (id, userData) => {
    return apiRequest(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  },

  deleteUser: async (id) => {
    return apiRequest(`/users/${id}`, {
      method: "DELETE",
    });
  },

  getUserAssets: async (id) => {
    return apiRequest(`/users/${id}/assets`);
  },

  getUserPermissions: async (id) => {
    return apiRequest(`/users/${id}/permissions`);
  },

  updateUserPermissions: async (id, overrides) => {
    return apiRequest(`/users/${id}/permissions`, {
      method: "PUT",
      body: JSON.stringify({ overrides }),
    });
  },

  deleteUserPermissionOverride: async (id, feature) => {
    return apiRequest(`/users/${id}/permissions/${encodeURIComponent(String(feature))}`, {
      method: "DELETE",
    });
  },

  // Sites
  getSites: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/sites${queryString ? `?${queryString}` : ""}`);
  },

  getSite: async (id) => {
    return apiRequest(`/sites/${id}`);
  },

  createSite: async (siteData) => {
    return apiRequest("/sites", {
      method: "POST",
      body: JSON.stringify(siteData),
    });
  },

  updateSite: async (id, siteData) => {
    return apiRequest(`/sites/${id}`, {
      method: "PUT",
      body: JSON.stringify(siteData),
    });
  },

  deleteSite: async (id) => {
    return apiRequest(`/sites/${id}`, {
      method: "DELETE",
    });
  },

  // Departments
  getDepartments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/departments${queryString ? `?${queryString}` : ""}`);
  },

  getDepartment: async (id) => {
    return apiRequest(`/departments/${id}`);
  },

  createDepartment: async (departmentData) => {
    return apiRequest("/departments", {
      method: "POST",
      body: JSON.stringify(departmentData),
    });
  },

  updateDepartment: async (id, departmentData) => {
    return apiRequest(`/departments/${id}`, {
      method: "PUT",
      body: JSON.stringify(departmentData),
    });
  },

  deleteDepartment: async (id) => {
    return apiRequest(`/departments/${id}`, {
      method: "DELETE",
    });
  },

  // Categories
  getCategories: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/categories${queryString ? `?${queryString}` : ""}`);
  },

  getCategory: async (id) => {
    return apiRequest(`/categories/${id}`);
  },

  createCategory: async (categoryData) => {
    return apiRequest("/categories", {
      method: "POST",
      body: JSON.stringify(categoryData),
    });
  },

  updateCategory: async (id, categoryData) => {
    return apiRequest(`/categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(categoryData),
    });
  },

  deleteCategory: async (id) => {
    return apiRequest(`/categories/${id}`, {
      method: "DELETE",
    });
  },

  // Locations
  getLocations: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/locations${queryString ? `?${queryString}` : ""}`);
  },

  getLocation: async (id) => {
    return apiRequest(`/locations/${id}`);
  },

  createLocation: async (locationData) => {
    return apiRequest("/locations", {
      method: "POST",
      body: JSON.stringify(locationData),
    });
  },

  updateLocation: async (id, locationData) => {
    return apiRequest(`/locations/${id}`, {
      method: "PUT",
      body: JSON.stringify(locationData),
    });
  },

  deleteLocation: async (id) => {
    return apiRequest(`/locations/${id}`, {
      method: "DELETE",
    });
  },

  // Handovers
  getHandovers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/handovers${queryString ? `?${queryString}` : ""}`);
  },

  sendEmailReport: async (data) => {
    return apiRequest("/handovers/email-report", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  getHandover: async (id) => {
    return apiRequest(`/handovers/${id}`);
  },

  createHandover: async (handoverData) => {
    return apiRequest("/handovers", {
      method: "POST",
      body: JSON.stringify(handoverData),
    });
  },

  acknowledgeHandover: async (id, acknowledgeData) => {
    return apiRequest(`/handovers/${id}/acknowledge`, {
      method: "PUT",
      body: JSON.stringify(acknowledgeData),
    });
  },

  sendHandoverReminder: async (id) => {
    return apiRequest(`/handovers/${id}/remind`, {
      method: "POST",
    });
  },

  // Maintenance
  getMaintenances: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/maintenance${queryString ? `?${queryString}` : ""}`);
  },

  getMaintenance: async (id) => {
    return apiRequest(`/maintenance/${id}`);
  },

  createMaintenance: async (maintenanceData) => {
    return apiRequest("/maintenance", {
      method: "POST",
      body: JSON.stringify(maintenanceData),
    });
  },

  updateMaintenance: async (id, maintenanceData) => {
    return apiRequest(`/maintenance/${id}`, {
      method: "PUT",
      body: JSON.stringify(maintenanceData),
    });
  },

  deleteMaintenance: async (id) => {
    return apiRequest(`/maintenance/${id}`, {
      method: "DELETE",
    });
  },

  // Permissions
  getPermissions: async () => {
    return apiRequest("/permissions");
  },

  getPermission: async (id) => {
    return apiRequest(`/permissions/${id}`);
  },

  createPermission: async (permissionData) => {
    return apiRequest("/permissions", {
      method: "POST",
      body: JSON.stringify(permissionData),
    });
  },

  updatePermission: async (id, permissionData) => {
    return apiRequest(`/permissions/${id}`, {
      method: "PUT",
      body: JSON.stringify(permissionData),
    });
  },

  updatePermissions: async (permissions) => {
    return apiRequest("/permissions", {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    });
  },

  deletePermission: async (id) => {
    return apiRequest(`/permissions/${id}`, {
      method: "DELETE",
    });
  },

  initializePermissions: async () => {
    return apiRequest("/permissions/initialize", {
      method: "POST",
    });
  },

  // Emails related to assets
  sendCheckoutEmail: async (emailData) => {
    return apiRequest("/assets/checkout-email", {
      method: "POST",
      body: JSON.stringify(emailData),
    });
  },

  sendCheckinEmail: async (emailData) => {
    return apiRequest("/assets/checkin-email", {
      method: "POST",
      body: JSON.stringify(emailData),
    });
  },

  sendBulkTransferEmail: async (transferData) => {
    return apiRequest("/assets/bulk-transfer-email", {
      method: "POST",
      body: JSON.stringify(transferData),
    });
  },

  // Settings
  getSettings: async () => {
    return apiRequest("/settings");
  },

  updateSettings: async (settingsData) => {
    return apiRequest("/settings", {
      method: "PUT",
      body: JSON.stringify(settingsData),
    });
  },

  // Notifications
  getNotifications: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/notifications${queryString ? `?${queryString}` : ""}`);
  },

  getNotificationCount: async () => {
    return apiRequest("/notifications/count");
  },

  markNotificationAsRead: async (id) => {
    return apiRequest(`/notifications/${id}/read`, {
      method: "PUT",
    });
  },

  markAllNotificationsAsRead: async () => {
    return apiRequest("/notifications/read-all", {
      method: "PUT",
    });
  },

  deleteNotification: async (id) => {
    return apiRequest(`/notifications/${id}`, {
      method: "DELETE",
    });
  },

  // Event Teams
  getEventTeams: async (params = {}) => {
    const token = getAuthToken();
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined && value !== "") {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    const url = `${EVENT_TEAMS_API_URL}${queryString ? `?${queryString}` : ""}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch event teams");
    }
    return data;
  },

  getEventTeamById: async (eventTeamId) => {
    if (!eventTeamId) {
      throw new Error("Event team id is required");
    }

    const token = getAuthToken();
    const response = await fetch(
      `${EVENT_TEAMS_API_URL}/${encodeURIComponent(String(eventTeamId))}`,
      {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch event team");
    }
    return data;
  },

  createEventTeam: async (eventTeamData) => {
    const token = getAuthToken();
    const response = await fetch(EVENT_TEAMS_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(eventTeamData),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to create event team");
    }
    return data;
  },

  updateEventTeam: async (eventTeamId, eventTeamData) => {
    const token = getAuthToken();
    const response = await fetch(`${EVENT_TEAMS_API_URL}/${eventTeamId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(eventTeamData),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to update event team");
    }
    return data;
  },

  deleteEventTeam: async (eventTeamId) => {
    const token = getAuthToken();
    const response = await fetch(`${EVENT_TEAMS_API_URL}/${eventTeamId}`, {
      method: "DELETE",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }

    if (!response.ok) {
      throw new Error(data.message || "Failed to delete event team");
    }
    return data;
  },

  // Matches
  getMatches: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/matches${queryString ? `?${queryString}` : ""}`);
  },

  createMatch: async (matchData) => {
    return apiRequest("/matches", {
      method: "POST",
      body: JSON.stringify(matchData),
    });
  },

  updateMatch: async (matchId, matchData) => {
    return apiRequest(`/matches/${matchId}`, {
      method: "PATCH",
      body: JSON.stringify(matchData),
    });
  },

  getMatchById: async (matchId) => {
    if (!matchId) {
      throw new Error("Match id is required");
    }
    return apiRequest(`/matches/${encodeURIComponent(String(matchId))}`);
  },

  // Job Types
  getJobTypes: async () => {
    const token = getAuthToken();
    const response = await axios.get(JOB_TYPES_API_URL, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return response.data;
  },

  deleteJobType: async (jobType) => {
    if (!jobType) {
      throw new Error("Job type is required");
    }

    const token = getAuthToken();
    const encodedJobType = encodeURIComponent(String(jobType).trim());

    try {
      const response = await axios.delete(`${API_BASE_URL}/job-types/${encodedJobType}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || error.message || "Failed to delete job type"
      );
    }
  },

  createJobType: async (jobType) => {
    const name = String(jobType || "").trim();
    if (!name) {
      throw new Error("Job type is required");
    }

    const token = getAuthToken();
    try {
      const response = await axios.post(
        `${API_BASE_URL}/job-types`,
        { jobType: name },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || error.message || "Failed to create job type"
      );
    }
  },

  updateJobType: async (id, jobType) => {
    const jobTypeId = String(id || "").trim();
    const name = String(jobType || "").trim();

    if (!jobTypeId) {
      throw new Error("Job type id is required");
    }
    if (!name) {
      throw new Error("Job type is required");
    }

    const token = getAuthToken();
    try {
      const response = await axios.put(
        `${API_BASE_URL}/job-types/${encodeURIComponent(jobTypeId)}`,
        { jobType: name },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || error.message || "Failed to update job type"
      );
    }
  },

  // Feedback Parameters
  getFeedbackParameters: async () => {
    return apiRequest("/feedback-parameters");
  },

  submitEventFeedback: async (payload) => {
    if (!payload || typeof payload !== "object") {
      throw new Error("Feedback payload is required");
    }

    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/event-feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }

    if (!response.ok) {
      const submitError = new Error(data.message || "Failed to submit feedback");
      submitError.status = response.status;
      throw submitError;
    }

    return {
      ...data,
      status: response.status,
    };
  },

  submitMatchFeedback: async (payload) => {
    if (!payload || typeof payload !== "object") {
      throw new Error("Match feedback payload is required");
    }

    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/match-feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    let data = {};
    try { data = await response.json(); } catch (error) { data = {}; }

    if (!response.ok) {
      const submitError = new Error(data.message || "Failed to submit match feedback");
      submitError.status = response.status;
      throw submitError;
    }

    return { ...data, status: response.status };
  },

  submitSupervisorFeedback: async (payload) => {
    if (!payload || typeof payload !== "object") {
      throw new Error("Supervisor feedback payload is required");
    }

    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/supervisor-feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    let data = {};
    try { data = await response.json(); } catch (error) { data = {}; }

    if (!response.ok) {
      const submitError = new Error(data.message || "Failed to submit supervisor feedback");
      submitError.status = response.status;
      throw submitError;
    }

    return { ...data, status: response.status };
  },

  getSupervisorFeedbacks: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/supervisor-feedback${queryString ? `?${queryString}` : ""}`);
  },

  getMatchFeedbacks: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/match-feedback${queryString ? `?${queryString}` : ""}`);
  },

  getEventFeedbacks: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return apiRequest(`/event-feedback${queryString ? `?${queryString}` : ""}`);
  },

  getMatchFeedbackByEventTeamId: async (eventTeamId) => {
    const normalizedId = String(eventTeamId || "").trim();
    if (!normalizedId) throw new Error("Event team id is required");

    const token = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/match-feedback/event-team/${encodeURIComponent(normalizedId)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    let data = {};
    try { data = await response.json(); } catch { data = {}; }

    if (!response.ok) {
      const err = new Error(data.message || "Failed to fetch match feedbacks");
      err.status = response.status;
      throw err;
    }

    return data;
  },

  getSupervisorFeedbackByMatchId: async (matchId, params = {}) => {
    const normalizedId = String(matchId || "").trim();
    if (!normalizedId) throw new Error("Match id is required");

    const queryParams = new URLSearchParams();
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();

    const token = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/supervisor-feedback/match/${encodeURIComponent(normalizedId)}${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    let data = {};
    try { data = await response.json(); } catch { data = {}; }

    if (!response.ok) {
      const err = new Error(data.message || "Failed to fetch supervisor feedback");
      err.status = response.status;
      throw err;
    }

    return data;
  },

  getEventFeedbackByEventTeamId: async (eventTeamId) => {
    const normalizedId = String(eventTeamId || "").trim();
    if (!normalizedId) {
      throw new Error("Event team id is required");
    }

    return apiRequest(
      `/event-feedback/event-team/${encodeURIComponent(normalizedId)}`
    );
  },

  getEventFeedbackByMatchId: async (matchId) => {
    const normalizedId = String(matchId || "").trim();
    if (!normalizedId) {
      throw new Error("Match id is required");
    }

    const url = `${API_BASE_URL}/match-feedback/match/${encodeURIComponent(normalizedId)}`;
    const token = getAuthToken();
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    let data = {};
    try { data = await response.json(); } catch { data = {}; }

    if (!response.ok) {
      const err = new Error(data.message || "Failed to fetch match feedback");
      err.status = response.status;
      throw err;
    }

    return data;
  },

  getEmployeeFeedbackSummary: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        String(params[key]).trim() !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    return apiRequest(
      `/event-feedback/employee-summary${queryString ? `?${queryString}` : ""}`
    );
  },

  getEmployeeFeedbackSummaryCached: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        String(params[key]).trim() !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    return apiRequest(
      `/event-feedback/employee-summary-cached${queryString ? `?${queryString}` : ""}`
    );
  },

  sendEmployeeFeedbackCsvEmail: async (payload) => {
    return apiRequest("/event-feedback/send-csv-email", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  sendEmployeeFeedbackPdfEmail: async (payload) => {
    return apiRequest("/event-feedback/send-pdf-email", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /** Expects body: { recipientEmails, pdfBase64, fileName?, subject?, filters? } */
  sendPerformanceReportPdfEmail: async (payload) => {
    return apiRequest("/performance-report/send-pdf-email", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  createFeedbackParameter: async (parameter) => {
    const name = String(parameter || "").trim();
    if (!name) {
      throw new Error("Feedback parameter is required");
    }

    return apiRequest("/feedback-parameters", {
      method: "POST",
      body: JSON.stringify({ parameter: name }),
    });
  },

  updateFeedbackParameter: async (id, parameterOrOptions) => {
    const parameterId = String(id || "").trim();
    if (!parameterId) {
      throw new Error("Feedback parameter id is required");
    }

    const body = {};
    if (typeof parameterOrOptions === "string") {
      const name = parameterOrOptions.trim();
      if (!name) {
        throw new Error("Feedback parameter is required");
      }
      body.parameter = name;
    } else if (parameterOrOptions && typeof parameterOrOptions === "object") {
      const name = String(
        parameterOrOptions.parameter ?? parameterOrOptions.name ?? ""
      ).trim();
      if (name) {
        body.parameter = name;
      }
      if (parameterOrOptions.isActive !== undefined) {
        body.isActive = Boolean(parameterOrOptions.isActive);
      }
      if (!body.parameter && body.isActive === undefined) {
        throw new Error("Send parameter name and/or isActive");
      }
    } else {
      throw new Error("Invalid feedback parameter update");
    }

    return apiRequest(`/feedback-parameters/${encodeURIComponent(parameterId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  deleteFeedbackParameter: async (id) => {
    const parameterId = String(id || "").trim();
    if (!parameterId) {
      throw new Error("Feedback parameter id is required");
    }

    return apiRequest(`/feedback-parameters/${encodeURIComponent(parameterId)}`, {
      method: "DELETE",
    });
  },

  /* ── Activity log helpers ─────────────────────────────────────────── */

  getProjectActivity: async (projectId, params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== "") {
        queryParams.append(key, params[key]);
      }
    });
    const qs = queryParams.toString();
    return apiRequest(`/projects/${projectId}/activity${qs ? `?${qs}` : ""}`);
  },

  getTaskActivity: async (taskId) => {
    return apiRequest(`/projects/tasks/${taskId}/activity`);
  },

  getFeatureActivity: async (projectId, featureId) => {
    return apiRequest(`/projects/${projectId}/features/${featureId}/activity`);
  },

};

export default api;
